import { SignedUploadForm } from "@coaching-culture/types";
import axios from "axios";

/**
 * Uploads go straight to S3 with a presigned POST form from the API. The
 * signed policy fixes the key, the Content-Type and the maximum size, so S3
 * refuses anything else (SEC-10).
 */

type Progress = (percent: number) => void;

/** Send a file to S3 using a presigned POST form. */
export async function postToSignedUpload(
  form: SignedUploadForm,
  file: Blob,
  onProgress?: Progress,
): Promise<void> {
  const body = new FormData();
  Object.entries(form.fields).forEach(([k, v]) => body.append(k, v));
  // S3 ignores any field after the file, so it goes last.
  body.append("file", file);

  await axios.post(form.url, body, {
    // Never send our API token to S3.
    transformRequest: (data, headers) => {
      delete headers?.common?.["Authorization"];
      delete headers?.["Authorization"];
      return data;
    },
    onUploadProgress: (e) => {
      if (onProgress != null && e.total != null && e.total > 0) {
        onProgress(Math.round((e.loaded * 100) / e.total));
      }
    },
  });
}

/** Ask the API for an upload form for this file, upload it, return the key. */
export async function uploadFile(file: File, onProgress?: Progress): Promise<string> {
  const { data } = await axios.post<{ key: string; upload: SignedUploadForm }>(
    "/api/uploads/signed",
    { filename: file.name },
  );
  await postToSignedUpload(data.upload, file, onProgress);
  return data.key;
}

/** A message for a failed upload, from the API or S3's XML error. */
export function uploadErrorMessage(err: any): string {
  const data = err?.response?.data;
  if (typeof data?.error === "string") {
    return data.error;
  }
  if (typeof data === "string" && data.includes("EntityTooLarge")) {
    return "That file is too large to upload.";
  }
  return "The upload failed. Please try again.";
}
